import React, { useState, useEffect } from "react";
import api from "../../../../api";
import Swal from "sweetalert2";
import { useAuth } from "../../../Layouts/context/Authcontext";
import { FaBalanceScale } from "react-icons/fa";
import { getRoundRobinBaseUrl } from "../../../../loadBalancer";
import KPIRatingTable from "./KPIRatingTable";

const KPIRating = () => {
  const today = new Date();
  const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const defaultMonth = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, "0")}`;

  const [overAllRating, setOverAllRating] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [employee, setEmployee] = useState(null);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [commentsData, setCommentsData] = useState({ comments: "", empStatus: "" });

  const { accessToken } = useAuth();

  const formatMonthYear = (monthStr) => {
    if (!monthStr) return "";
    let year, month;
    if (monthStr.includes("-")) {
      [year, month] = monthStr.split("-");
    } else if (monthStr.includes("/")) {
      [month, year] = monthStr.split("/");
    } else {
      return "";
    }
    if (month.length === 1) {
      month = "0" + month;
    }
    return `${year}-${month}`;
  };

  const checkExistingComment = async (empId, month) => {
    const monthDate = formatMonthYear(month);
    try {
      const baseUrls = getRoundRobinBaseUrl();
      const url = `${baseUrls}/pqa/getRatingAgreeAndNotAgree`;

      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { month: monthDate },
      });

      if (res.data.data) {
        setHasSubmitted(true);
        setCommentsData({
          comments: res.data.data.comments || "N/A",
          empStatus: res.data.data.empStatus || "N/A",
        });
      } else {
        setHasSubmitted(false);
        setCommentsData({ comments: "", empStatus: "" });
      }
    } catch (error) {
      console.error("Error checking existing comment:", error);
      setHasSubmitted(false);
      setCommentsData({ comments: "", empStatus: "" });
    }
  };

  const fetchData = async (month = "") => {
    try {
      setLoading(true);
      setHasSubmitted(false);
      setCommentsData({ comments: "", empStatus: "" });

      const baseUrls = getRoundRobinBaseUrl();
      const empUrl = `${baseUrls}/pqa/monthly/report`;
      const empBody = { month };

      const empResponse = await api.post(empUrl, empBody, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (empResponse.data.success && empResponse.data.data) {
        const foundEmployee = empResponse.data.data.find(emp =>
          ["BE", "ME", "EE"].includes(emp.performanceBand)
        );
        setEmployee(foundEmployee || null);

        if (foundEmployee && month) {
          await checkExistingComment(foundEmployee.empId, month);
        } else {
          setHasSubmitted(false);
          setCommentsData({ comments: "", empStatus: "" });
        }
      } else {
        setEmployee(null);
        setHasSubmitted(false);
        setCommentsData({ comments: "", empStatus: "" });
      }

      let url = `${baseUrls}/userDashboard/getMonthyProductivityPercentage`;
      if (month) {
        url += `?month=${month}`;
      }

      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.data.success) {
        setOverAllRating(response.data.OverAllRating || "");
      } else {
        setOverAllRating("");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setOverAllRating("");
      setEmployee(null);
      setHasSubmitted(false);
      setCommentsData({ comments: "", empStatus: "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMonth) {
      fetchData(selectedMonth);
    } else {
      setLoading(false);
    }
  }, [accessToken, selectedMonth]);

  const getLegendInfo = (rating) => {
    const numericRating = Number(rating);
    if (numericRating >= 1 && numericRating < 3) return { label: "BE", bgColor: "bg-[#EE1D23]", fullForm: "Below Expectations" };
    if (numericRating >= 3 && numericRating < 5) return { label: "ME", bgColor: "bg-[#1A2B5C]", fullForm: "Meeting Expectations" };
    if (numericRating === 5) return { label: "EE", bgColor: "bg-[#808080]", fullForm: "Exceeding Expectations" };
    return { label: "", bgColor: "bg-gray-100", fullForm: "" };
  };

  const { label, fullForm } = getLegendInfo(overAllRating);

  const year = today.getFullYear();
  const month = today.getMonth();
  let maxMonth = month === 0 ? `${year - 1}-12` : `${year}-${String(month).padStart(2, "0")}`;

  return (
    <div className="p-8 flex flex-col items-center">
      <div className="flex justify-center gap-4 mb-6 w-full max-w-2xl">
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => {
            setSelectedMonth(e.target.value);
            setHasSubmitted(false);
            setCommentsData({ comments: "", empStatus: "" });
            setShowCommentBox(false);
            setCommentText("");
          }}
          max={maxMonth}
          className="bg-white/20 backdrop-blur-md rounded-xl px-4 py-3 w-full md:w-60 text-gray-800 focus:outline-none shadow-inner hover:shadow-lg transition-all"
        />
        <button
          onClick={() => {
            setSelectedMonth(defaultMonth);
            setEmployee(null);
            setOverAllRating("");
            setShowCommentBox(false);
            setCommentText("");
            setHasSubmitted(false);
            setCommentsData({ comments: "", empStatus: "" });
          }}
          className="px-4 py-3 rounded-xl bg-gradient-to-r from-gray-700 to-gray-900 text-white font-bold text-sm shadow-md transform hover:scale-105 hover:shadow-xl transition-all duration-300"
        >
          Reset
        </button>
      </div>

      <div className="mb-6 grid grid-cols-3 gap-6 text-center w-full max-w-2xl">
        <div className="bg-[#EE1D23] p-3 rounded-lg shadow-md">
          <p className="text-lg font-bold text-white">BE</p>
          <p className="text-sm text-white">Below Expectations</p>
        </div>
        <div className="bg-[#1A2B5C] p-3 rounded-lg shadow-md">
          <p className="text-lg font-bold text-white">ME</p>
          <p className="text-sm text-white">Meeting Expectations</p>
        </div>
        <div className="bg-[#808080] p-3 rounded-lg shadow-md">
          <p className="text-lg font-bold text-white">EE</p>
          <p className="text-sm text-white">Exceeding Expectations</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center p-8">Loading...</div>
      ) : (
        <>
          <div className="relative rounded-3xl overflow-hidden transition-transform transform hover:scale-105 hover:shadow-2xl shadow-xl border-2 border-transparent bg-clip-padding p-1 w-full md:w-1/2"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05))",
              borderImage: "linear-gradient(135deg, #EE1D23, #1A2B5C, #808080) 1"
            }}
          >
            <div className="relative p-6 text-center rounded-3xl backdrop-blur-xl bg-white/20 shadow-inner border border-white/20">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#EE1D23] to-[#1A2B5C] rounded-full flex items-center justify-center mb-4 shadow-lg border border-white/40">
                  <FaBalanceScale className="text-3xl text-white" />
                </div>
                <h6 className="text-2xl font-extrabold text-gray-800 drop-shadow-md">Current Month KPI Rating</h6>
                <p className="text-lg italic text-gray-600">Employee Performance Details</p>
              </div>
              <div className="mt-6 w-full flex flex-col items-center justify-center rounded-xl border-2 border-white/30 p-4 bg-white/30 shadow backdrop-blur-lg">
                {label && (
                  <>
                    <p className="font-bold text-xl text-gray-800">{label}</p>
                    <p className="text-sm text-gray-600">{fullForm}</p>
                  </>
                )}
                {employee && (
                  <div className="mt-4 flex flex-col items-center">
                    <p
                      className={`relative text-2xl font-extrabold mt-1 px-8 py-3 rounded-2xl backdrop-blur-md bg-gradient-to-br from-white/20 to-white/5 shadow-inner border transition-all duration-500 overflow-hidden
                        ${employee.performanceBand === "BE"
                          ? "text-[#EE1D23] border-[#EE1D23] shadow-[0_0_20px_#EE1D23] hover:shadow-[0_0_25px_#EE1D23]"
                          : employee.performanceBand === "ME"
                          ? "text-[#1A2B5C] border-[#1A2B5C] shadow-[0_0_20px_#1A2B5C] hover:shadow-[0_0_25px_#1A2B5C]"
                          : employee.performanceBand === "EE"
                          ? "text-[#808080] border-[#808080] shadow-[0_0_20px_#808080] hover:shadow-[0_0_25px_#808080]"
                          : "text-gray-800 border-gray-300"
                        }`}
                    >
                      <span className="relative z-10">{employee.performanceBand}</span>
                      <span className="absolute inset-0 rounded-2xl opacity-20 blur-md bg-gradient-to-r from-white/30 to-transparent animate-pulse"></span>
                    </p>
                  </div>
                )}

                {selectedMonth && employee && (
                  <div className="flex justify-center gap-4 mt-4">
                    <button
                      onClick={async () => {
                        if (!employee || !selectedMonth) return;
                        setIsSending(true);
                        try {
                          const baseUrls = getRoundRobinBaseUrl();
                          const url = `${baseUrls}/pqa/createRatingAgreeAndNotAgree`;
                          const body = {
                            ...employee,
                            month: selectedMonth,
                            empStatus: "Agree",
                            comments: "Satisfaction",
                          };
                          await api.post(url, body, {
                            headers: { Authorization: `Bearer ${accessToken}` },
                          });
                          Swal.fire({
                            icon: "success",
                            title: "Thank you!",
                            text: "Your agreement has been submitted successfully.",
                            confirmButtonColor: "#1A2B5C",
                          });
                          await fetchData(selectedMonth);
                        } catch (error) {
                          console.error("Error sending Agree:", error);
                          Swal.fire({
                            icon: "error",
                            title: "Oops!",
                            text: "Something went wrong. Please try again.",
                            confirmButtonColor: "#EE1D23",
                          });
                        } finally {
                          setIsSending(false);
                        }
                      }}
                      disabled={hasSubmitted}
                      className="px-5 py-2 rounded-lg bg-[#1A2B5C] text-white font-bold text-sm shadow-md transform hover:scale-105 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Agree
                    </button>

                    <button
                      onClick={() => setShowCommentBox(true)}
                      disabled={hasSubmitted}
                      className="px-5 py-2 rounded-lg bg-[#808080] text-white font-bold text-sm shadow-md transform hover:scale-105 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Disagree
                    </button>
                  </div>
                )}

                {showCommentBox && (
                  <div className="w-full mt-4 animate-fade-in">
                    <label className="block text-gray-800 font-bold mb-1">Your Comments (Disagree)</label>
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows={3}
                      className="w-full p-3 border border-gray-500 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-[#1A2B5C] transition-all"
                      placeholder="Write your comments here..."
                    />
                    <button
                      onClick={async () => {
                        if (!employee || !selectedMonth) return;
                        setIsSending(true);
                        try {
                          const baseUrls = getRoundRobinBaseUrl();
                          const url = `${baseUrls}/pqa/createRatingAgreeAndNotAgree`;
                          const body = {
                            ...employee,
                            month: selectedMonth,
                            empStatus: "Disagree",
                            comments: commentText || "Disagree",
                          };
                          await api.post(url, body, {
                            headers: { Authorization: `Bearer ${accessToken}` },
                          });
                          Swal.fire({
                            icon: "success",
                            title: "Thank you!",
                            text: "Your comment has been submitted successfully.",
                            confirmButtonColor: "#1A2B5C",
                          });
                          await fetchData(selectedMonth);
                          setShowCommentBox(false);
                          setCommentText("");
                        } catch (error) {
                          console.error("Error sending comment:", error);
                          Swal.fire({
                            icon: "error",
                            title: "Oops!",
                            text: "Something went wrong. Please try again.",
                            confirmButtonColor: "#EE1D23",
                          });
                        } finally {
                          setIsSending(false);
                        }
                      }}
                      disabled={isSending}
                      className="mt-3 px-5 py-2 rounded-lg bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23] text-white font-bold text-sm shadow-md transform hover:scale-105 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSending ? "Sending..." : "Send"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {hasSubmitted && commentsData.empStatus === "Agree" && (
            <div className="mt-6 text-green-700 font-semibold text-lg text-center">
              ✅ You agreed with the current month's KPI rating.
            </div>
          )}

          {hasSubmitted && commentsData.empStatus === "Disagree" && (
            <KPIRatingTable commentsData={commentsData} />
          )}

          {!loading && selectedMonth && !employee && (
            <div className="text-center text-red-600 mt-6 font-semibold">
              No data available for this month.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default KPIRating;
